import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'

import LineEditAltIcon from 'components/Luxkit/Icons/line/LineEditAltIcon'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { formatOccupantsAsGuests } from 'lib/occupancyUtils'
import moment from 'moment'
import Clickable from 'components/Common/Clickable'
import TextButton from 'components/Luxkit/Button/TextButton'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateTrigger'
import { getTotalRoomsString } from 'components/Search/utils'

interface Props {
  className?: string;
  onEdit: () => void;
  location?: string;
  isDestinationPage?: boolean;
}

const StyledContainer = styled(Clickable)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${rem(10)} ${rem(25)} ${rem(20)} ${rem(25)};
  background-color: ${props => props.theme.palette.neutral.eight};
  width: 100%;

  &.landing-page-test {
    padding: ${rem(20)} ${rem(25)} ${rem(20)} ${rem(25)};
  }
`

const StyledDetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${rem(2)};
`

const StyledDescriptionsSection = styled.div`
  display: flex;
  column-gap: ${rem(8)};
`
function prettyDates(checkinDate?: moment.Moment, checkoutDate?: moment.Moment): string {
  if (checkinDate && checkoutDate) {
    const checkOut = checkoutDate.day() >= 10 ? checkoutDate.format(DMY_CASUAL_FORMAT) : checkoutDate.format('D MMM YYYY')
    let checkinFormat = checkinDate.day() >= 10 ? 'DD' : 'D'
    if (checkinDate.year() === checkoutDate.year()) {
      checkinFormat += checkinDate.month() === checkoutDate.month() ? '' : ' MMM'
    } else {
      checkinFormat += ' MMM YYYY'
    }
    return `${checkinDate.format(checkinFormat)} - ${checkOut}`
  }
  return 'Anytime'
}

function HotelSearchHeaderBar(props: Props) {
  const { onEdit, className, location, isDestinationPage } = props
  const {
    checkinDate,
    checkoutDate,
    occupancies,
    searchTargetLabel,
    dateSearchOptionId,
    flexibleMonths,
    flexibleNights,
    userSelectedFlexibleMonths,
  } = useContext(GlobalSearchStateContext)

  const searchBarDescription = useMemo(() => {
    const bulletPointSeparator = '\u2022'
    const datesDetails = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE ?
      prettyFlexibleDates(userSelectedFlexibleMonths ? flexibleMonths : undefined, flexibleNights) :
      prettyDates(checkinDate, checkoutDate)
    const occupancyDetails = formatOccupantsAsGuests(...occupancies)
    const roomsDetails = getTotalRoomsString(occupancies)
    const occupancyWithRoomDetails = `${occupancyDetails} ${roomsDetails}`

    return [
      datesDetails,
      bulletPointSeparator,
      occupancyWithRoomDetails,
    ]
  }, [checkinDate, checkoutDate, occupancies, dateSearchOptionId, flexibleMonths, flexibleNights, userSelectedFlexibleMonths])

  return (
    <StyledContainer className={cn(className, { 'landing-page-test': isDestinationPage })} onClick={onEdit}>
      <StyledDetailsSection>
        <Heading variant="heading6">{searchTargetLabel ?? location}</Heading>
        <StyledDescriptionsSection>
          {searchBarDescription.map((description, index) => (
            <Caption key={index} variant="large">
              {description}
            </Caption>
          ))}
        </StyledDescriptionsSection>
      </StyledDetailsSection>
      <TextButton nonInteractable kind="tertiary" endIcon={<LineEditAltIcon />}>Edit</TextButton>
    </StyledContainer>
  )
}

export default HotelSearchHeaderBar
